import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Grid,
  TextField,
} from "@mui/material";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import { useMemo, useState } from "react";
import { Field, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  AgreementRequest,
  AgreementResponse,
} from "services/KnowfullyAdminClient";
import {
  AgreementStatusIds,
  Severity,
  SnackbarMessageProps,
} from "types/common-types";

type AgreementUserInputFormProps = {
  agreement: AgreementResponse | undefined;
  onSubmit: (values: AgreementRequest) => Promise<void | { [FORM_ERROR]: any }>;
  onCancel: () => void;
};

const { Form } = withTypes<AgreementRequest>();

const AgreementUserInputForm = ({
  agreement,
  onSubmit,
  onCancel,
}: AgreementUserInputFormProps) => {
  const { formatMessage: t } = useIntl();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const init = useMemo(() => {
    const values = agreement
      ? {
          id: agreement.id,
          organizationId: agreement.organizationId,
          name: agreement.name,
          displayName: agreement.displayName,
          agreementStatusId: agreement.agreementStatusId,
          manualReplacements: agreement.manualReplacements,
          templateVersionId: agreement.templateVersionId,
        }
      : {};

    return new AgreementRequest(values);
  }, [agreement]);

  const isDisabled =
    agreement?.agreementStatusId?.toUpperCase() ===
      AgreementStatusIds.Deactivated ||
    agreement?.agreementStatusId?.toUpperCase() === AgreementStatusIds.Active;

  const copyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "copiedToClipboardMessage" }),
          Severity.Success,
        ),
      );
    } catch (error) {
      console.error(error);
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "copyToClipboardFailed" }),
          Severity.Error,
        ),
      );
    }
  };

  const jsonTemplate = `
  {
    "template": [
      {
        "key": "some_text",
        "key_type": "text",
        "mandatory": true,
        "display_value": "Some Text"
      }
    ]
  }`;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({
        handleSubmit,
        submitting,
        submitError,
        values,
        dirty,
        form: { change },
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Field name="manualReplacements" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      multiline
                      rows={10}
                      fullWidth
                      label={t({ id: "jsonUserInputLabel" })}
                      required
                      disabled={isDisabled}
                      InputLabelProps={{ required: true }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Box marginTop={2} marginLeft={2}>
                  <Alert severity="error">{submitError}</Alert>
                </Box>
              )}
            </Grid>

            <DialogActions>
              <Button onClick={async () => await copyToClipboard(jsonTemplate)}>
                {t({ id: "copyTemplate" })}
              </Button>

              {submitting && <CircularProgress size={24} />}
              <Button disabled={submitting || !dirty} onClick={onCancel}>
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting || !dirty || isDisabled}
              >
                {t({ id: "saveButton" })}
              </Button>
            </DialogActions>

            <SnackbarMessage
              snackbarMessage={snackbarMessage}
              setSnackbarMessage={setSnackbarMessage}
            />
          </form>
        );
      }}
    />
  );
};

export default AgreementUserInputForm;
